import { Fragment, createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import type { Booking, CustomerProfile } from '../api/schemas';
import { describeLevelMix, fileVocabulary, levelMix, rosterParts, type RosterPart } from '../lib/customerFile';
import { useBusiness } from '../lib/useBusiness';
import { useCustomerProfiles } from '../lib/useCustomerProfiles';
import { CustomerFileDrawer } from './CustomerFileDrawer';

type OpenCustomer = { customerId: string; name: string };

type RosterFileValue = {
  profiles: ReadonlyMap<string, CustomerProfile>;
  /** Whether this vertical grades clients by level (studios, gyms). */
  graded: boolean;
  /** A staff id's name on the active roster, for "Prefiere a Marco". */
  staffName: (staffId: string) => string | undefined;
  open: (booking: Booking) => void;
};

const EMPTY: RosterFileValue = {
  profiles: new Map(),
  graded: false,
  staffName: () => undefined,
  open: () => undefined,
};
const RosterFileContext = createContext<RosterFileValue>(EMPTY);

/**
 * Gives a roster page one profile read and one ficha drawer: every attendee
 * row shows its level from the same map, and tapping a name opens the file
 * where the coach can mark the level before the class starts.
 */
export function RosterFileProvider({ children }: { children: ReactNode }) {
  const business = useBusiness();
  const profiles = useCustomerProfiles();
  const graded = fileVocabulary(business?.category).levels;
  const staff = business?.staff;
  const [openCustomer, setOpenCustomer] = useState<OpenCustomer | null>(null);

  const value = useMemo<RosterFileValue>(() => {
    const names = new Map((staff ?? []).map((member) => [member.id, member.name]));
    return {
      profiles,
      graded,
      staffName: (staffId) => names.get(staffId),
      open: (booking) => {
        if (!booking.customerId) return;
        setOpenCustomer({ customerId: booking.customerId, name: booking.customerName ?? 'Cliente' });
      },
    };
  }, [profiles, graded, staff]);

  return (
    <RosterFileContext.Provider value={value}>
      {children}
      <CustomerFileDrawer customer={openCustomer} onClose={() => setOpenCustomer(null)} />
    </RosterFileContext.Provider>
  );
}

export function useRosterFile(): RosterFileValue {
  return useContext(RosterFileContext);
}

/** How each part of the line under a name is marked: injuries and level stand out, the rest reads quiet. */
const PART_CLASS: Record<RosterPart['kind'], string | undefined> = {
  injuries: 'client-injuries',
  level: 'client-level',
  favorite: undefined,
  tag: undefined,
};

/**
 * The attendee's name, one line under it (injuries first, then level, then the
 * favorite coach when it is not this class's coach, then tags), and the door to
 * their ficha. The line truncates rather than wraps.
 */
export function AttendeeName({ booking }: { booking: Booking }) {
  const { profiles, graded, staffName, open } = useRosterFile();
  const name = booking.customerName ?? 'Sin nombre';
  if (!booking.customerId) return <span className="grow">{name}</span>;
  const parts = rosterParts(profiles.get(booking.customerId), {
    graded,
    staffName,
    currentStaffId: booking.staffId,
  });
  return (
    <button
      type="button"
      className="coach-attendee-open grow"
      aria-label={`Abrir ficha de ${name}`}
      onClick={() => open(booking)}
    >
      <span className="coach-attendee-name">{name}</span>
      {parts.length > 0 ? (
        <span className="row-sub">
          {parts.map((part, index) => (
            <Fragment key={`${part.kind}-${part.text}`}>
              {index > 0 ? ' · ' : null}
              {PART_CLASS[part.kind] ? <span className={PART_CLASS[part.kind]}>{part.text}</span> : part.text}
            </Fragment>
          ))}
        </span>
      ) : null}
    </button>
  );
}

/** "Nivel: 3 principiantes · 1 avanzado", only where the vertical grades and someone is graded. */
export function ClassLevelMix({ bookings }: { bookings: readonly Booking[] }) {
  const { profiles, graded } = useRosterFile();
  if (!graded || bookings.length === 0) return null;
  const description = describeLevelMix(levelMix(bookings.map((booking) => booking.customerId), profiles));
  if (!description) return null;
  return (
    <p className="row-sub class-level-mix">
      <span className="class-level-mix-label">Nivel</span> {description}
    </p>
  );
}
